import { resolveStoredFileUrl } from "utils/storedFileUrl";
import { Fade, Icon, MenuItem, Modal, Select } from "@mui/material";
import PropTypes from "prop-types";
import { useContext, useEffect, useState } from "react";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftInputNumber from "components/SoftInputNumberUpgrade";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import { nombreCompraDetalle } from "utils/presentacionCompra";

const modalSx = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  display: "flex",
  flexDirection: "column",
  width: { xs: "calc(100% - 24px)", sm: "min(680px, calc(100% - 48px))" },
  maxHeight: { xs: "calc(100dvh - 24px)", sm: "min(90dvh, 760px)" },
  overflow: "hidden",
  borderRadius: { xs: "20px", sm: "24px" },
  backgroundColor: "#ffffff",
  boxShadow: "0 24px 70px rgba(15, 23, 42, 0.24)",
};

const labelSx = {
  display: "block",
  mb: 0.65,
  lineHeight: 1.35,
  whiteSpace: "normal",
};

export default function ModalLicitacionProducto({
  open,
  handleClose,
  productoElegido,
  handleChangeProducto,
  handleChangeServicio,
  cotizada,
  licitacionHabilitada,
  productoAnterior,
}) {
  const [producto, setProducto] = useState({});
  const [guardando, setGuardando] = useState(false);
  const { handleSnackbar } = useContext(MessageManager);
  const requierePack = Boolean(producto?.requierePack);

  const nombreProducto = productoElegido ? nombreCompraDetalle(productoElegido) : "Cargar cotización";
  const unidadProducto =
    (productoElegido?.requierePack && productoElegido?.unidadPack) ||
    productoElegido?.unidadNombre ||
    productoElegido?.materiaPrima?.tipoUnidad?.nombre ||
    "Sin unidad";
  const formularioHabilitado = !cotizada && licitacionHabilitada;
  const guardarDeshabilitado =
    guardando ||
    !(Number.isFinite(Number(producto?.precio)) && Number(producto?.precio) > 0) ||
    (producto?.alternativo && !String(producto?.nombreAlternativo || "").trim()) ||
    !(Number.isInteger(Number(producto?.plazoEntrega)) && Number(producto?.plazoEntrega) > 0);

  useEffect(() => {
    setProducto({
      ...productoElegido,
      alternativo: productoElegido?.requierePack ? 0 : productoElegido?.materiaPrimaAlternativo ? 1 : 0,
      nombreAlternativo: productoElegido?.requierePack ? "" : productoElegido?.nombreAlternativo,
      plazoEntrega:
        productoElegido?.plazoEntrega ?? productoAnterior?.cotizaciones?.[0]?.plazoEntrega ?? "",
    });
  }, [productoElegido, productoAnterior]);

  const handleChanges = (event) => {
    if (event.target.name === "alternativo" && requierePack) return;

    if (event.target.name === "alternativo") {
      setProducto((previous) => ({
        ...previous,
        alternativo: event.target.value,
        nombreAlternativo: "",
      }));
      return;
    }

    setProducto((previous) => ({
      ...previous,
      [event.target.name]: event.target.value,
    }));
  };

  const handleGuardar = () => {
    if (guardarDeshabilitado || !formularioHabilitado) return;

    setGuardando(true);
    try {
      const productoSanitizado = requierePack
        ? { ...producto, alternativo: 0, nombreAlternativo: "" }
        : producto;

      if (productoSanitizado?.materiaPrima) {
        handleChangeProducto(productoSanitizado);
      } else {
        handleChangeServicio(productoSanitizado);
      }
      handleSnackbar("Item agregado correctamente", "success");
      setTimeout(() => setGuardando(false), 1500);
    } catch (error) {
      handleSnackbar("Error al guardar los datos", "error");
      setGuardando(false);
    }
  };

  return (
    <Modal
      aria-labelledby="cotizacion-producto-title"
      aria-describedby="cotizacion-producto-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <SoftBox
          component="form"
          onSubmit={(event) => {
            event.preventDefault();
            handleGuardar();
          }}
          sx={modalSx}
        >
          <SoftBox
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            gap={1.5}
            px={{ xs: 2, sm: 3 }}
            py={{ xs: 1.75, sm: 2.25 }}
            sx={{ borderBottom: "1px solid #e9ecef", flex: "0 0 auto" }}
          >
            <SoftBox minWidth={0}>
              <SoftTypography
                id="cotizacion-producto-title"
                variant="h6"
                color="dark"
                fontWeight="bold"
                sx={{ whiteSpace: "normal", overflowWrap: "anywhere", lineHeight: 1.3 }}
              >
                {nombreProducto}
              </SoftTypography>
              <SoftTypography
                id="cotizacion-producto-description"
                variant="caption"
                color="text"
                display="block"
                mt={0.4}
              >
                Completá los datos de tu oferta para este producto.
              </SoftTypography>
            </SoftBox>
            <SoftButton
              type="button"
              variant="outlined"
              color="dark"
              circular
              iconOnly
              onClick={handleClose}
              aria-label="Cerrar formulario de cotización"
              sx={{ minWidth: "40px !important", width: 40, height: 40, flex: "0 0 auto" }}
            >
              <Icon>close</Icon>
            </SoftButton>
          </SoftBox>

          <SoftBox
            px={{ xs: 2, sm: 3 }}
            py={{ xs: 2, sm: 2.5 }}
            sx={{ overflowY: "auto", overscrollBehavior: "contain", flex: "1 1 auto", minHeight: 0 }}
          >
            <SoftBox
              display="flex"
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              flexDirection={{ xs: "column", sm: "row" }}
              gap={1}
              p={1.5}
              mb={2}
              sx={{ border: "1px solid #e9ecef", borderRadius: "12px", backgroundColor: "#f8f9fa" }}
            >
              <SoftBox minWidth={0}>
                <SoftTypography variant="caption" color="text" display="block">
                  Cantidad solicitada
                </SoftTypography>
                <SoftTypography variant="button" color="dark" fontWeight="bold" display="block" mt={0.3}>
                  {productoElegido?.cantidadVerificada ?? 0} {unidadProducto}
                </SoftTypography>
                {productoElegido?.requierePack && <SoftTypography variant="caption" display="block">
                  1 {unidadProducto} = {Number(productoElegido?.totalMP) / Number(productoElegido?.cantidad)} {productoElegido?.materiaPrima?.tipoUnidad?.abreviatura}
                </SoftTypography>}
              </SoftBox>
              <SoftTypography variant="caption" color="primary" fontWeight="bold">
                * Campos obligatorios
              </SoftTypography>
            </SoftBox>

            {productoElegido?.file && (
              <SoftButton
                type="button"
                variant="text"
                color="dark"
                size="small"
                onClick={() => window.open(resolveStoredFileUrl(productoElegido.file), "_blank")}
                sx={{ mb: 2, px: 0 }}
              >
                <Icon sx={{ mr: 0.6 }}>attach_file</Icon>
                Ver documento adjunto
              </SoftButton>
            )}

            <SoftBox
              display="grid"
              gridTemplateColumns={{ xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))" }}
              columnGap={2}
              rowGap={2}
            >
              <SoftBox minWidth={0}>
                <SoftTypography component="label" htmlFor="alternativo" variant="caption" fontWeight="bold" sx={labelSx}>
                  Producto/Servicio ofrecido
                </SoftTypography>
                <Select
                  id="alternativo"
                  name="alternativo"
                  fullWidth
                  value={producto?.alternativo || 0}
                  onChange={handleChanges}
                  disabled={!formularioHabilitado || requierePack}
                  inputProps={{ "aria-label": "Producto o servicio ofrecido" }}
                  sx={{
                    cursor: "pointer",
                    minHeight: 40,
                    "& .MuiSelect-select": { whiteSpace: "normal", overflowWrap: "anywhere" },
                  }}
                >
                  <MenuItem value={0}>Mismo que solicitado</MenuItem>
                  <MenuItem value={1} disabled={requierePack}>
                    Alternativo
                  </MenuItem>
                </Select>
              </SoftBox>

              <SoftBox minWidth={0}>
                <SoftTypography
                  component="label"
                  htmlFor="nombreAlternativo"
                  variant="caption"
                  fontWeight="bold"
                  sx={labelSx}
                >
                  Producto/Servicio alternativo
                </SoftTypography>
                <SoftInput
                  placeholder={formularioHabilitado ? "Nombre..." : ""}
                  type="text"
                  name="nombreAlternativo"
                  disabled={!producto?.alternativo || !formularioHabilitado || requierePack}
                  onChange={handleChanges}
                  value={producto?.nombreAlternativo || ""}
                  inputProps={{
                    id: "nombreAlternativo",
                    maxLength: 250,
                    disabled: !producto?.alternativo || !formularioHabilitado || requierePack,
                    "aria-label": "Nombre alternativo",
                  }}
                />
              </SoftBox>

              <SoftBox minWidth={0}>
                <SoftTypography component="label" htmlFor="precioUnitario" variant="caption" fontWeight="bold" sx={labelSx}>
                  Precio por {unidadProducto} *
                </SoftTypography>
                <SoftInputNumber
                  icon={{ component: "attach_money", direction: "left" }}
                  name="precio"
                  onChange={handleChanges}
                  value={producto?.precio ?? null}
                  disabled={!formularioHabilitado}
                  inputProps={{
                    id: "precioUnitario",
                    maxLength: 16,
                    disabled: !formularioHabilitado,
                    "aria-label": "Precio unitario",
                  }}
                />
              </SoftBox>

              <SoftBox minWidth={0}>
                <SoftTypography component="label" htmlFor="plazoEntrega" variant="caption" fontWeight="bold" sx={labelSx}>
                  Plazo de entrega en días corridos *
                </SoftTypography>
                <SoftInput
                  placeholder={
                    formularioHabilitado
                      ? productoAnterior?.plazoEntrega
                        ? `Ej: ${productoAnterior.plazoEntrega} días corridos`
                        : "Plazo de entrega..."
                      : ""
                  }
                  type="number"
                  name="plazoEntrega"
                  onChange={(event) => {
                    const value = event.target.value;
                    if (/^\d*$/.test(value)) {
                      handleChanges({
                        target: { name: "plazoEntrega", value: value ? parseInt(value, 10) : "" },
                      });
                    }
                  }}
                  value={producto?.plazoEntrega || ""}
                  disabled={!formularioHabilitado}
                  inputProps={{
                    id: "plazoEntrega",
                    min: 1,
                    disabled: !formularioHabilitado,
                    "aria-label": "Plazo de entrega en días corridos",
                  }}
                />
              </SoftBox>

              <SoftBox minWidth={0} sx={{ gridColumn: "1 / -1" }}>
                <SoftTypography component="label" htmlFor="observacion" variant="caption" fontWeight="bold" sx={labelSx}>
                  Observación
                </SoftTypography>
                <SoftInput
                  placeholder="Observaciones..."
                  type="text"
                  name="observacion"
                  onChange={handleChanges}
                  value={producto?.observacion || ""}
                  multiline
                  rows={3}
                  inputProps={{
                    id: "observacion",
                    maxLength: 1024,
                    disabled: !formularioHabilitado,
                    "aria-label": "Observación",
                  }}
                  disabled={!formularioHabilitado}
                />
              </SoftBox>
            </SoftBox>
          </SoftBox>

          {formularioHabilitado && (
            <SoftBox
              display="flex"
              justifyContent="flex-end"
              px={{ xs: 2, sm: 3 }}
              py={{ xs: 1.5, sm: 2 }}
              sx={{ borderTop: "1px solid #e9ecef", backgroundColor: "#ffffff", flex: "0 0 auto" }}
            >
              <SoftButton
                type="submit"
                color="uproGreen"
                disabled={guardarDeshabilitado}
                sx={{ width: { xs: "100%", sm: "auto" }, minWidth: { sm: 190 }, minHeight: 42 }}
              >
                {guardando ? "Guardando..." : "Guardar cambios"}
              </SoftButton>
            </SoftBox>
          )}
        </SoftBox>
      </Fade>
    </Modal>
  );
}

ModalLicitacionProducto.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  productoElegido: PropTypes.object,
  handleChangeProducto: PropTypes.func,
  handleChangeServicio: PropTypes.func,
  cotizada: PropTypes.bool,
  licitacionHabilitada: PropTypes.bool,
  productoAnterior: PropTypes.object,
};

ModalLicitacionProducto.defaultProps = {
  productoElegido: null,
  handleChangeProducto: () => {},
  handleChangeServicio: () => {},
  cotizada: false,
  licitacionHabilitada: false,
  productoAnterior: null,
};
